import { writeConfigForApi, clearConfigForApi } from './config.js';
import { log, logImmediate } from './log.js';
import { createBridgeConnection } from './connection/create-bridge-connection.js';
import { installE2eCertificateKeyCommand } from './e2e-certificates/key-command.js';
import { openE2eCertificateImportUrl } from './e2e-certificates/open-import-url.js';
import { requestCliImmediateShutdown } from './runtime/cli-process-interrupt.js';
import { abortActiveGitChildProcesses } from './git/git-runtime.js';
import { CliSqliteInterrupted } from './sqlite/cli-database.js';
import type { RunBridgeOptions } from './run-bridge-options.js';

export async function runConnectedBridge(
  options: RunBridgeOptions,
  restartWithoutAuth: (options: RunBridgeOptions) => Promise<void>
): Promise<void> {
  const {
    apiUrl,
    workspaceId,
    authToken,
    refreshToken,
    justAuthenticated,
    worktreesRootPath,
    e2eCertificate,
  } = options;
  const firehoseServerUrl = options.firehoseServerUrl ?? options.proxyServerUrl;
  let cleanupKeyCommand: (() => void) | undefined;
  /** Set after {@link createBridgeConnection} resolves so SIGINT during startup only sets the interrupt flag until then. */
  let bridgeClose: (() => Promise<void>) | null = null;
  let shutdownStarted = false;

  const onSignal = (kind: 'interrupt' | 'stop') => {
    requestCliImmediateShutdown();
    abortActiveGitChildProcesses();
    cleanupKeyCommand?.();
    if (shutdownStarted) return;
    shutdownStarted = true;
    logImmediate(
      kind === 'interrupt'
        ? 'Keyboard interrupt (Ctrl+C) — stopping…'
        : 'Stop requested — shutting down…'
    );
    if (bridgeClose) {
      void bridgeClose().finally(() => process.exit(0));
      return;
    }
    // Startup still in flight — exit immediately; in-flight work checks the shutdown flag.
    process.exit(0);
  };
  const onSigInt = () => onSignal('interrupt');
  const onSigTerm = () => onSignal('stop');
  process.on('SIGINT', onSigInt);
  process.on('SIGTERM', onSigTerm);

  let handle: Awaited<ReturnType<typeof createBridgeConnection>>;
  try {
    handle = await createBridgeConnection({
      apiUrl,
      workspaceId: workspaceId!,
      authToken: authToken!,
      refreshToken,
      firehoseServerUrl,
      justAuthenticated,
      worktreesRootPath,
      e2eCertificate,
      log,
      persistTokens: (t) => {
        writeConfigForApi(apiUrl, {
          workspaceId: workspaceId!,
          token: t.token,
          refreshToken: t.refreshToken,
        });
      },
      onAuthInvalid: () => {
        cleanupKeyCommand?.();
        log('[Bridge service] Access token invalid or revoked; re-authenticating…');
        clearConfigForApi(apiUrl);
        void handle.close().then(() => {
          void restartWithoutAuth({ apiUrl, firehoseServerUrl, worktreesRootPath, e2eCertificate });
        });
      },
    });
  } catch (e) {
    process.off('SIGINT', onSigInt);
    process.off('SIGTERM', onSigTerm);
    if (e instanceof CliSqliteInterrupted) {
      process.exit(0);
    }
    throw e;
  }

  bridgeClose = () => handle.close();

  if (e2eCertificate) {
    let openingCertificate = false;
    cleanupKeyCommand = installE2eCertificateKeyCommand({
      log,
      onInterrupt: onSigInt,
      onOpenCertificate: () => {
        if (openingCertificate) return;
        openingCertificate = true;
        void openE2eCertificateImportUrl({
          apiUrl,
          workspaceId: workspaceId!,
          certificate: e2eCertificate,
          log,
        }).finally(() => {
          openingCertificate = false;
        });
      },
    });
  }
}
